import React, { useEffect, useRef } from 'react';
import { CHAT } from '@/constants/testIds';

// Custom black cursor + sparkle trail. Disabled on touch / reduced-motion.
export const CursorTrail = () => {
  const dotRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });
  const enabledRef = useRef(false);

  useEffect(() => {
    const isTouch = window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isTouch) {
      document.body.classList.add('no-custom-cursor');
      return undefined;
    }
    document.body.classList.remove('no-custom-cursor');
    enabledRef.current = true;

    const dot = dotRef.current;
    const canvas = canvasRef.current;
    if (!dot || !canvas) return undefined;
    const ctx = canvas.getContext('2d');

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      sizeRef.current = { w, h, dpr };
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();

    function emit(x, y) {
      if (reduced) return;
      const n = 2 + Math.floor(Math.random() * 3);
      for (let i = 0; i < n; i += 1) {
        particlesRef.current.push({
          x: x + (Math.random() - 0.5) * 6,
          y: y + (Math.random() - 0.5) * 6,
          vx: (Math.random() - 0.5) * 0.6,
          vy: 0.2 + Math.random() * 0.7,
          size: 1 + Math.random() * 1.2,
          life: 0,
          ttl: 18 + Math.floor(Math.random() * 14),
        });
      }
    }

    function onMove(e) {
      const x = e.clientX;
      const y = e.clientY;
      dot.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      emit(x, y);
    }

    function step() {
      const { w, h } = sizeRef.current;
      ctx.clearRect(0, 0, w, h);
      const ps = particlesRef.current;
      for (let i = ps.length - 1; i >= 0; i -= 1) {
        const p = ps[i];
        p.life += 1;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.02;
        const k = 1 - p.life / p.ttl;
        if (k <= 0) {
          ps.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.globalAlpha = Math.max(0, k) * 0.85;
        ctx.fillStyle = '#000';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('resize', resize);
      document.body.classList.add('no-custom-cursor');
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="cursor-trail-canvas"
        aria-hidden="true"
        data-testid={CHAT.cursorTrailCanvas}
      />
      <div ref={dotRef} className="custom-cursor" data-testid={CHAT.customCursor} aria-hidden="true" />
    </>
  );
};
